import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputBase,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient.ts';
import { useEffectiveStudentId } from '../hooks/useEffectiveStudentId.ts';
import { useTutorSession } from '../context/TutorSessionContext.tsx';
import { logTutorSessionEvent } from '../utils/tutorSessionEvents.ts';
import RichText from '../components/RichText.tsx';

type YearRow = { id: string; year: string | number };
type SubjectRow = { id: string; name: string; year_id: string | null };
type CategoryRow = {
  id: string;
  name: string;
  parent_category_id: string | null;
  subject_id: string | null;
};

type Lesson = {
  id: string;
  title: string | null;
  created_at: string | null;
  updated_at?: string | null;
  category_id: string | null;
  subject_id: string | null;
  content: string | null;
  metadata: Record<string, any>;
  previewHtml: string;
  tagNames: string[];
  resolvedSubjectId: string | null;
  resolvedYearId: string | null;
  categoryPath: string | null;
};

type SortMode = 'recent' | 'name' | 'year' | 'subject';

const MAX_VISIBLE_TAGS = 3;
const PREVIEW_FALLBACK = '<p style="color:#64748b;font-size:12px;">No preview</p>';

const extractQuestionIdFromAttrs = (attrs: string): string | null => {
  const m = attrs.match(/\bid\s*=\s*["']([^"']+)["']/i);
  return m?.[1] ?? null;
};

const extractQuestionIdsFromHtml = (html: string | null | undefined): string[] => {
  if (!html) return [];
  const ids = new Set<string>();
  const rx = /<question\b([^>]*)\/?>/gi;
  let match: RegExpExecArray | null = null;
  while ((match = rx.exec(html)) !== null) {
    const id = extractQuestionIdFromAttrs(match[1] ?? '');
    if (id) ids.add(id);
  }
  return Array.from(ids);
};

const buildQuestionPreviewBlock = (questionId: string, promptHtml?: string): string => {
  if (promptHtml && promptHtml.trim().length > 0) {
    return `
      <div style="margin:6px 0;padding:8px 10px;border:1px dashed #93c5fd;border-radius:10px;background:#eff6ff;">
        <div style="font-size:11px;color:#475569;margin-bottom:6px;">Embedded question • #${questionId.slice(0, 8)}...</div>
        <div>${promptHtml}</div>
      </div>
    `;
  }
  return `
    <div style="margin:6px 0;padding:8px 10px;border:1px dashed #93c5fd;border-radius:10px;background:#eff6ff;color:#1e3a8a;font-size:12px;">
      Embedded question • #${questionId.slice(0, 8)}...
    </div>
  `;
};

const normalisePreviewHtml = (
  html: string | null | undefined,
  questionPromptById: Record<string, string>,
): string => {
  if (!html) return PREVIEW_FALLBACK;
  const replaceTag = (attrs: string) => {
    const qid = extractQuestionIdFromAttrs(attrs);
    if (!qid) return PREVIEW_FALLBACK;
    return buildQuestionPreviewBlock(qid, questionPromptById[qid]);
  };
  return html
    .replace(/<question\b([^>]*)>([\s\S]*?)<\/question>/gi, (_m, attrs) => replaceTag(attrs))
    .replace(/<question\b([^>]*)\/>/gi, (_m, attrs) => replaceTag(attrs));
};

const extractTagNames = (metadata: any): string[] => {
  if (!metadata || typeof metadata !== 'object') return [];
  const candidates = [metadata.tags, metadata.tagNames, metadata.tag_names];
  const out = new Set<string>();
  candidates.forEach((value) => {
    if (!Array.isArray(value)) return;
    value.forEach((item) => {
      if (typeof item === 'string' && item.trim()) out.add(item.trim());
      if (item && typeof item === 'object' && typeof item.name === 'string' && item.name.trim()) {
        out.add(item.name.trim());
      }
    });
  });
  return Array.from(out).sort((a, b) => a.localeCompare(b));
};

const expandCategoryIds = (ids: string[], childrenById: Record<string, string[]>) => {
  const out = new Set<string>(ids);
  const queue = [...ids];
  while (queue.length) {
    const parent = queue.shift() as string;
    (childrenById[parent] ?? []).forEach((kid) => {
      if (!out.has(kid)) {
        out.add(kid);
        queue.push(kid);
      }
    });
  }
  return out;
};

const LessonsPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const { tutorSession } = useTutorSession();
  const { studentId, loading: studentIdLoading } = useEffectiveStudentId();

  const [years, setYears] = useState<YearRow[]>([]);
  const [subjects, setSubjects] = useState<SubjectRow[]>([]);
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [allLessons, setAllLessons] = useState<Lesson[]>([]);

  const [loadingMeta, setLoadingMeta] = useState(true);
  const [loadingResults, setLoadingResults] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [query, setQuery] = useState('');
  const [selectedYearIds, setSelectedYearIds] = useState<string[]>([]);
  const [selectedSubjectIds, setSelectedSubjectIds] = useState<string[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [sortMode, setSortMode] = useState<SortMode>('recent');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const [expandedTreeNodes, setExpandedTreeNodes] = useState<string[]>([]);

  const subjectById = useMemo(
    () => Object.fromEntries(subjects.map((s) => [s.id, s])) as Record<string, SubjectRow>,
    [subjects],
  );
  const yearById = useMemo(
    () => Object.fromEntries(years.map((y) => [y.id, y])) as Record<string, YearRow>,
    [years],
  );
  const categoryById = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.id, c])) as Record<string, CategoryRow>,
    [categories],
  );

  const childrenByCategoryId = useMemo(() => {
    const out: Record<string, string[]> = {};
    categories.forEach((c) => {
      if (c.parent_category_id) (out[c.parent_category_id] ||= []).push(c.id);
    });
    return out;
  }, [categories]);

  useEffect(() => {
    if (studentIdLoading) return;
    (async () => {
      setLoadingMeta(true);
      setLoadingResults(true);
      setErr(null);

      if (!studentId) {
        setErr('Student record not found.');
        setLoadingMeta(false);
        setLoadingResults(false);
        return;
      }

      const { data: student, error: studentErr } = await supabase
        .from('students')
        .select('company_id')
        .eq('id', studentId)
        .maybeSingle();
      if (studentErr || !student?.company_id) {
        setErr(studentErr?.message ?? 'Student company not found.');
        setLoadingMeta(false);
        setLoadingResults(false);
        return;
      }
      const { data: studentSubjects } = await supabase
        .from('student_subjects')
        .select('subject_id')
        .eq('student_id', studentId);
      const enrolledSubjectIds = (studentSubjects ?? [])
        .map((r: any) => r.subject_id as string)
        .filter(Boolean);

      let subjectQuery = supabase.from('subjects').select('id, name, year_id');
      if (enrolledSubjectIds.length) subjectQuery = subjectQuery.in('id', enrolledSubjectIds);
      const { data: subjectRows, error: subjectErr } = await subjectQuery;
      if (subjectErr) {
        setErr(subjectErr.message);
        setLoadingMeta(false);
        setLoadingResults(false);
        return;
      }
      const downloadedSubjects = ((subjectRows ?? []) as any[]).map((s) => ({
        id: s.id,
        name: s.name,
        year_id: s.year_id ?? null,
      })) as SubjectRow[];
      setSubjects(downloadedSubjects);

      const allSubjectIds = downloadedSubjects.map((s) => s.id);
      let downloadedCategories: CategoryRow[] = [];
      if (allSubjectIds.length > 0) {
        const { data: rootCats } = await supabase
          .from('categories')
          .select('id, name, parent_category_id, subject_id')
          .in('subject_id', allSubjectIds)
          .is('parent_category_id', null);
        const allMap: Record<string, CategoryRow> = {};
        const queue: string[] = [];
        (rootCats ?? []).forEach((c: any) => {
          allMap[c.id] = c as CategoryRow;
          queue.push(c.id);
        });
        while (queue.length) {
          const batch = Array.from(new Set(queue.splice(0, 50)));
          const { data: kids } = await supabase
            .from('categories')
            .select('id, name, parent_category_id, subject_id')
            .in('parent_category_id', batch);
          (kids ?? []).forEach((c: any) => {
            if (!allMap[c.id]) {
              allMap[c.id] = c as CategoryRow;
              queue.push(c.id);
            }
          });
        }
        downloadedCategories = Object.values(allMap);
      }
      setCategories(downloadedCategories);
      const downloadedCategoryById = Object.fromEntries(
        downloadedCategories.map((c) => [c.id, c]),
      ) as Record<string, CategoryRow>;
      const downloadedCategorySubjectById: Record<string, string | null> = {};
      downloadedCategories.forEach((cat) => {
        let cursor: CategoryRow | undefined = cat;
        let hops = 0;
        while (cursor && hops < 8) {
          if (cursor.subject_id) {
            downloadedCategorySubjectById[cat.id] = cursor.subject_id;
            return;
          }
          cursor = cursor.parent_category_id
            ? downloadedCategoryById[cursor.parent_category_id]
            : undefined;
          hops += 1;
        }
        downloadedCategorySubjectById[cat.id] = null;
      });
      const downloadedCategoryPathById: Record<string, string> = {};
      downloadedCategories.forEach((cat) => {
        const parts: string[] = [];
        let cursor: CategoryRow | undefined = cat;
        let hops = 0;
        while (cursor && hops < 8) {
          parts.unshift(cursor.name);
          cursor = cursor.parent_category_id
            ? downloadedCategoryById[cursor.parent_category_id]
            : undefined;
          hops += 1;
        }
        downloadedCategoryPathById[cat.id] = parts.join(' > ');
      });
      const downloadedSubjectById = Object.fromEntries(
        downloadedSubjects.map((s) => [s.id, s]),
      ) as Record<string, SubjectRow>;

      const visibleYearIds = Array.from(
        new Set(downloadedSubjects.map((s) => s.year_id).filter((id): id is string => Boolean(id))),
      );
      if (visibleYearIds.length > 0) {
        const { data: yearRows } = await supabase
          .from('years')
          .select('id, year')
          .in('id', visibleYearIds)
          .order('year', { ascending: true });
        setYears((yearRows ?? []) as YearRow[]);
      } else {
        setYears([]);
      }

      const { data: lessonRows, error: lessonErr } = await supabase
        .from('lessons')
        .select('id, title, created_at, updated_at, category_id, subject_id, content, metadata')
        .eq('company_id', student.company_id);
      if (lessonErr) {
        setErr(lessonErr.message);
        setLoadingMeta(false);
        setLoadingResults(false);
        return;
      }

      let visibleLessons = (lessonRows ?? []) as any[];
      if (enrolledSubjectIds.length > 0) {
        const allowed = new Set(enrolledSubjectIds);
        visibleLessons = visibleLessons.filter((l) => {
          const sid = (l.subject_id as string | null) ?? null;
          const categorySid =
            l.category_id && downloadedCategorySubjectById[l.category_id]
              ? downloadedCategorySubjectById[l.category_id]
              : null;
          return Boolean((sid && allowed.has(sid)) || (categorySid && allowed.has(categorySid)));
        });
      }

      const questionIds = Array.from(
        new Set(visibleLessons.flatMap((l) => extractQuestionIdsFromHtml(l.content))),
      );
      const questionPromptById: Record<string, string> = {};
      if (questionIds.length > 0) {
        const BATCH = 150;
        for (let i = 0; i < questionIds.length; i += BATCH) {
          const chunk = questionIds.slice(i, i + BATCH);
          const { data: qs } = await supabase
            .from('questions')
            .select('id, prompt')
            .in('id', chunk);
          (qs ?? []).forEach((q: any) => {
            if (q?.id && typeof q.prompt === 'string') questionPromptById[q.id] = q.prompt;
          });
        }
      }

      const mapped: Lesson[] = visibleLessons.map((l) => {
        const categoryId = (l.category_id as string | null) ?? null;
        const subjectId =
          (l.subject_id as string | null) ??
          (categoryId ? downloadedCategorySubjectById[categoryId] ?? null : null);
        return {
          id: l.id,
          title: l.title ?? null,
          created_at: l.created_at ?? null,
          updated_at: l.updated_at ?? null,
          category_id: categoryId,
          subject_id: (l.subject_id as string | null) ?? null,
          content: l.content ?? null,
          metadata: (l.metadata ?? {}) as Record<string, any>,
          previewHtml: normalisePreviewHtml(l.content ?? null, questionPromptById),
          tagNames: extractTagNames(l.metadata),
          resolvedSubjectId: subjectId,
          resolvedYearId: subjectId ? downloadedSubjectById[subjectId]?.year_id ?? null : null,
          categoryPath: categoryId ? downloadedCategoryPathById[categoryId] ?? null : null,
        };
      });

      setAllLessons(mapped);
      setLoadingMeta(false);
      setLoadingResults(false);
    })();
  }, [studentId, studentIdLoading]);

  const baseFilteredLessons = useMemo(() => {
    const queryLower = query.trim().toLowerCase();
    const selectedYears = new Set(selectedYearIds);
    const selectedSubjects = new Set(selectedSubjectIds);
    const selectedCategories = selectedCategoryIds.length
      ? expandCategoryIds(selectedCategoryIds, childrenByCategoryId)
      : null;

    return allLessons.filter((l) => {
      if (queryLower) {
        const hay = [
          l.title ?? '',
          (l.metadata?.description as string) ?? '',
          l.tagNames.join(' '),
        ]
          .join(' ')
          .toLowerCase();
        if (!hay.includes(queryLower)) return false;
      }

      if (selectedYears.size > 0) {
        if (!l.resolvedYearId || !selectedYears.has(l.resolvedYearId)) return false;
      }

      if (selectedSubjects.size > 0) {
        if (!l.resolvedSubjectId || !selectedSubjects.has(l.resolvedSubjectId)) return false;
      }

      if (selectedCategories) {
        if (!l.category_id || !selectedCategories.has(l.category_id)) return false;
      }
      return true;
    });
  }, [
    allLessons,
    childrenByCategoryId,
    query,
    selectedCategoryIds,
    selectedSubjectIds,
    selectedYearIds,
  ]);

  const filteredLessons = useMemo(() => {
    const filtered = [...baseFilteredLessons];

    filtered.sort((a, b) => {
      if (sortMode === 'name') return (a.title ?? '').localeCompare(b.title ?? '');
      if (sortMode === 'year') {
        const ay = a.resolvedYearId ? Number(yearById[a.resolvedYearId]?.year ?? 0) : 0;
        const by = b.resolvedYearId ? Number(yearById[b.resolvedYearId]?.year ?? 0) : 0;
        if (by !== ay) return by - ay;
        return (a.title ?? '').localeCompare(b.title ?? '');
      }
      if (sortMode === 'subject') {
        const asn = a.resolvedSubjectId ? subjectById[a.resolvedSubjectId]?.name ?? '' : '';
        const bsn = b.resolvedSubjectId ? subjectById[b.resolvedSubjectId]?.name ?? '' : '';
        return asn.localeCompare(bsn) || (a.title ?? '').localeCompare(b.title ?? '');
      }
      return (
        new Date(b.updated_at ?? b.created_at ?? 0).getTime() -
        new Date(a.updated_at ?? a.created_at ?? 0).getTime()
      );
    });

    return filtered;
  }, [baseFilteredLessons, sortMode, subjectById, yearById]);

  const filterCounts = useMemo(() => {
    const yearCount: Record<string, number> = {};
    const subjectCount: Record<string, number> = {};
    const categoryCount: Record<string, number> = {};

    allLessons.forEach((l) => {
      if (l.resolvedYearId) yearCount[l.resolvedYearId] = (yearCount[l.resolvedYearId] ?? 0) + 1;
      if (l.resolvedSubjectId) subjectCount[l.resolvedSubjectId] = (subjectCount[l.resolvedSubjectId] ?? 0) + 1;
      if (l.category_id) categoryCount[l.category_id] = (categoryCount[l.category_id] ?? 0) + 1;
    });
    return { yearCount, subjectCount, categoryCount };
  }, [allLessons]);

  const toggleMultiSelect = (id: string, selected: string[], setter: (v: string[]) => void) => {
    setter(selected.includes(id) ? selected.filter((v) => v !== id) : [...selected, id]);
  };
  const toggleTreeNode = (id: string) => {
    setExpandedTreeNodes((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  };
  const isTreeNodeExpanded = (id: string) => expandedTreeNodes.includes(id);

  const contextForLesson = (lesson: Lesson) => {
    const chips: string[] = [];
    if (lesson.resolvedYearId && yearById[lesson.resolvedYearId]) chips.push(String(yearById[lesson.resolvedYearId].year));
    if (lesson.resolvedSubjectId && subjectById[lesson.resolvedSubjectId]) chips.push(subjectById[lesson.resolvedSubjectId].name);
    if (lesson.categoryPath) chips.push(lesson.categoryPath);
    return chips;
  };

  const secondaryLineForLesson = (lesson: Lesson) => {
    const topics = Number(lesson.metadata?.topicCount ?? lesson.metadata?.topics ?? 0);
    const questions = Number(lesson.metadata?.questionCount ?? lesson.metadata?.questions ?? 0);
    const minutes = Number(lesson.metadata?.estimatedMinutes ?? lesson.metadata?.minutes ?? 0);
    const parts: string[] = [];
    if (topics > 0) parts.push(`${topics} topics`);
    if (questions > 0) parts.push(`${questions} questions`);
    if (minutes > 0) parts.push(`~${minutes} min`);
    return parts.join(' • ');
  };

  const subjectsByYearId = useMemo(() => {
    const out: Record<string, SubjectRow[]> = {};
    subjects.forEach((s) => {
      if (s.year_id) (out[s.year_id] ||= []).push(s);
    });
    return out;
  }, [subjects]);
  const rootCategoriesBySubjectId = useMemo(() => {
    const out: Record<string, CategoryRow[]> = {};
    categories
      .filter((c) => c.parent_category_id === null && c.subject_id)
      .forEach((c) => {
        (out[c.subject_id as string] ||= []).push(c);
      });
    return out;
  }, [categories]);

  const renderCategoryTree = (category: CategoryRow, depth: number): React.ReactNode => {
    const children = (childrenByCategoryId[category.id] ?? [])
      .map((id) => categoryById[id])
      .filter(Boolean);
    const nodeId = `cat:${category.id}`;
    const expanded = isTreeNodeExpanded(nodeId);
    return (
      <React.Fragment key={nodeId}>
        <Stack direction="row" alignItems="center" sx={{ minHeight: 34, pl: depth * 2 }}>
          {children.length > 0 ? (
            <IconButton size="small" onClick={() => toggleTreeNode(nodeId)} sx={{ mr: 0.5 }}>
              {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          ) : (
            <Box sx={{ width: 32, mr: 0.5 }} />
          )}
          <Checkbox
            size="small"
            checked={selectedCategoryIds.includes(category.id)}
            onChange={() => toggleMultiSelect(category.id, selectedCategoryIds, setSelectedCategoryIds)}
          />
          <Typography variant="body2">{category.name}</Typography>
        </Stack>
        {expanded && children.map((c) => renderCategoryTree(c, depth + 1))}
      </React.Fragment>
    );
  };

  const filterSidebar = (
    <Box sx={{ width: { xs: 320, md: 300 }, p: { xs: 2, md: 0 } }}>
      <Paper
        variant="outlined"
        sx={{ mb: 1.25, p: 1.5, borderRadius: 2, bgcolor: 'background.paper', borderColor: 'rgb(180, 180, 180)' }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }} color="text.secondary">
          Select years, subjects, and categories
        </Typography>
        <Box
          sx={{
            maxHeight: 360,
            overflowY: 'auto',
            pr: 1,
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': { width: 8 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(100,116,139,0.4)', borderRadius: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          }}
        >
          {years.map((year) => {
            const yearNode = `year:${year.id}`;
            const yearExpanded = isTreeNodeExpanded(yearNode);
            const yearSubjects = subjectsByYearId[year.id] ?? [];
            return (
              <React.Fragment key={yearNode}>
                <Stack direction="row" alignItems="center" sx={{ minHeight: 34 }}>
                  {yearSubjects.length > 0 ? (
                    <IconButton size="small" onClick={() => toggleTreeNode(yearNode)} sx={{ mr: 0.5 }}>
                      {yearExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                    </IconButton>
                  ) : (
                    <Box sx={{ width: 32, mr: 0.5 }} />
                  )}
                  <Checkbox
                    size="small"
                    checked={selectedYearIds.includes(year.id)}
                    onChange={() => toggleMultiSelect(year.id, selectedYearIds, setSelectedYearIds)}
                  />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {String(year.year)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({filterCounts.yearCount[year.id] ?? 0})
                  </Typography>
                </Stack>

                {yearExpanded &&
                  yearSubjects.map((subject) => {
                    const subjectNode = `subject:${subject.id}`;
                    const subjectExpanded = isTreeNodeExpanded(subjectNode);
                    const roots = rootCategoriesBySubjectId[subject.id] ?? [];
                    return (
                      <React.Fragment key={subjectNode}>
                        <Stack direction="row" alignItems="center" sx={{ minHeight: 34, pl: 2 }}>
                          {roots.length > 0 ? (
                            <IconButton size="small" onClick={() => toggleTreeNode(subjectNode)} sx={{ mr: 0.5 }}>
                              {subjectExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                            </IconButton>
                          ) : (
                            <Box sx={{ width: 32, mr: 0.5 }} />
                          )}
                          <Checkbox
                            size="small"
                            checked={selectedSubjectIds.includes(subject.id)}
                            onChange={() =>
                              toggleMultiSelect(subject.id, selectedSubjectIds, setSelectedSubjectIds)
                            }
                          />
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {subject.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ({filterCounts.subjectCount[subject.id] ?? 0})
                          </Typography>
                        </Stack>
                        {subjectExpanded && roots.map((cat) => renderCategoryTree(cat, 2))}
                      </React.Fragment>
                    );
                  })}
              </React.Fragment>
            );
          })}
        </Box>
      </Paper>

    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5">Lessons</Typography>
        <Tabs
          value="/lessons"
          onChange={(_e, value) => navigate(value)}
          sx={{
            minHeight: 40,
            '& .MuiTabs-indicator': { backgroundColor: 'primary.main' },
          }}
        >
          <Tab
            value="/lessons"
            label="Find Lessons"
            sx={{ textTransform: 'none', minHeight: 40, color: '#111827' }}
          />
          <Tab
            value="/lessons/assigned"
            label="Your Lessons"
            sx={{ textTransform: 'none', minHeight: 40, color: '#111827' }}
          />
        </Tabs>
      </Stack>

      {(loadingMeta || loadingResults) && <LinearProgress sx={{ mb: 2 }} />}
      {err && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {err}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
          <Paper
            component="form"
            onSubmit={(e) => e.preventDefault()}
            sx={{
              borderRadius: 999,
              border: '1px solid',
              borderColor: 'rgb(180, 180, 180)',
              px: 1,
              py: 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              boxShadow: 'none',
              flex: 1,
              bgcolor: 'background.paper',
            }}
          >
            <IconButton size="small">
              <SearchIcon />
            </IconButton>
            <InputBase
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search lessons"
              sx={{ flex: 1, px: 0.5 }}
            />
          </Paper>

          <Stack direction="row" spacing={1} alignItems="center">
            {isMdDown && (
              <Button variant="outlined" startIcon={<TuneIcon />} onClick={() => setMobileFiltersOpen(true)}>
                Filters
              </Button>
            )}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="lesson-sort-label">Sort</InputLabel>
              <Select
                labelId="lesson-sort-label"
                label="Sort"
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value as SortMode)}
              >
                <MenuItem value="recent">Recently Updated</MenuItem>
                <MenuItem value="name">Name A-Z</MenuItem>
                <MenuItem value="year">Year</MenuItem>
                <MenuItem value="subject">Subject</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5 }}>
        {!isMdDown && (
          <Box
            sx={{
              width: 300,
              flexShrink: 0,
              maxHeight: 'calc(100vh - 220px)',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': { width: 8 },
              '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(100,116,139,0.4)', borderRadius: 8 },
              '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            }}
          >
            {filterSidebar}
          </Box>
        )}

        {!isMdDown && <Divider orientation="vertical" flexItem />}

        <Box sx={{ flex: 1, minWidth: 0 }}>

          {filteredLessons.length === 0 && !loadingMeta && !loadingResults ? (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
              No lessons matched your filters.
            </Paper>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 2.75,
              }}
            >
              {filteredLessons.map((lesson) => {
                const metaChips = contextForLesson(lesson);
                const secondary = secondaryLineForLesson(lesson);
                return (
                  <Paper
                    key={lesson.id}
                    variant="outlined"
                    onClick={() => {
                      if (tutorSession?.id) {
                        void logTutorSessionEvent(tutorSession.id, 'lesson_view', {
                          lesson_id: lesson.id,
                          title: lesson.title ?? '',
                          category_id: lesson.category_id ?? null,
                          viewed: true,
                          source: 'lessons_card_click',
                        });
                      }
                      navigate(`/lessons/${lesson.id}`);
                    }}
                    sx={{
                      width: '100%',
                      borderRadius: 2.5,
                      cursor: 'pointer',
                      bgcolor: 'background.paper',
                      overflow: 'hidden',
                      border: '2px solid rgb(180, 180, 180)',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'box-shadow 150ms ease, transform 120ms ease, border-color 120ms ease',
                      '&:hover': {
                        boxShadow: 2,
                        transform: 'translateY(-2px)',
                        borderColor: 'primary.light',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        height: 180,
                        bgcolor: 'grey.50',
                        borderBottom: (t) => `1px solid ${t.palette.divider}`,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          inset: 0,
                          p: 1,
                          transform: 'scale(0.56)',
                          transformOrigin: 'top left',
                          width: '180%',
                          height: '180%',
                          '& img': { maxWidth: '100%', height: 'auto' },
                          '& table': { width: '100%', borderCollapse: 'collapse' },
                          '& *': { maxWidth: '100%' },
                        }}
                      >
                        <RichText html={lesson.previewHtml || PREVIEW_FALLBACK} />
                      </Box>
                      <Box
                        sx={{
                          pointerEvents: 'none',
                          position: 'absolute',
                          inset: 0,
                          background: `
                            linear-gradient(to top,
                              rgba(2, 6, 23, 0.1) -100%,
                              rgba(2, 6, 23, 0.0) 60%
                            )
                          `,
                        }}
                      />
                    </Box>

                    <Box sx={{ px: 1.5, py: 1.25, flex: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <MenuBookIcon color="action" fontSize="small" />
                        <Typography variant="body1" sx={{ fontWeight: 500 }} noWrap title={lesson.title ?? ''}>
                          {lesson.title || 'Untitled'}
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                        {metaChips.map((chip) => (
                          <Chip key={chip} size="small" label={chip} variant="outlined" />
                        ))}
                      </Stack>

                      {secondary && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {secondary}
                        </Typography>
                      )}

                      {lesson.tagNames.length > 0 && (
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
                          {lesson.tagNames.slice(0, MAX_VISIBLE_TAGS).map((tag) => (
                            <Chip key={tag} size="small" label={tag} variant="outlined" />
                          ))}
                          {lesson.tagNames.length > MAX_VISIBLE_TAGS && (
                            <Chip size="small" label={`+${lesson.tagNames.length - MAX_VISIBLE_TAGS}`} variant="outlined" />
                          )}
                        </Stack>
                      )}
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      <Drawer
        anchor="left"
        open={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        PaperProps={{ sx: { width: 340 } }}
      >
        {filterSidebar}
      </Drawer>
    </Box>
  );
};

export default LessonsPage;
